/**
 * <LiveChat> — écrire à son masteur pendant le live (organism).
 *
 * Le chat d'aujourd'hui (`LiveChat`, une boîte à 90 % de l'écran) posé sur le socle de
 * conversation de l'élève : la ligne d'en-tête et sa croix, puis `<ConversationPanel>` (le fil, le
 * composeur). Il se porte dans la feuille de `<SamSheet>` : feuille basse sur un téléphone,
 * panneau latéral sur un grand écran, la scène ou l'activité visible à côté.
 *
 * Un fil par tuteur du roster, comme les vignettes de l'ancien chat : celui du tuteur de la séance
 * (ouvert par défaut), celui de chaque autre tuteur avec qui l'élève a un fil, et, dans un cours
 * partagé, celui de tout le groupe (« Tous »). Dès qu'il y en a plus d'un, le choix se fait par une
 * puce chacun sous l'en-tête (`<LiveChatChannels>`), qui dit alors « Messages » ; un seul fil, pas
 * de puce, et l'en-tête porte la capsule et le nom du tuteur.
 *
 * Chaque puce porte le compte de ses messages non lus, comme la vignette de l'ancien chat : le chat
 * s'ouvre sur le fil qui en a, les autres disent aussi s'ils attendent une lecture. Le fil d'une
 * puce touchée, tant que son premier relevé n'est pas là, montre sa forme (`loading`), pas « Aucun
 * message ».
 *
 * Dans « Tous », chaque message de l'autre côté dit qui l'a écrit (`message.sender`, groupé par
 * `groupConversation`) : un autre élève a son avatar, jamais son prénom (on ne nomme pas un autre
 * élève, Wajih, 17/10) ; le tuteur garde son nom et sa capsule. Un message sans auteur se lit au nom
 * du tuteur, comme avant.
 *
 * Et dans « Tous », l'élève lit sans écrire : seul le tuteur y publie, comme dans l'ancien chat
 * (`CommonChat.renderComposer`, qui remplaçait le champ de l'élève par une phrase). Le composeur
 * laisse sa place à cette phrase, et à un lien qui ouvre le fil du tuteur.
 *
 * Les pièces jointes et le fil de la messagerie, quand l'écran les passe (défaut : texte seul) : le
 * bouton de pièce jointe du composeur (`attachSources`), une pièce reçue qui s'ouvre
 * (`onOpenAttachment`), un vocal qui s'écoute (`voicePlayback`) et, chargé, se règle (curseur et
 * vitesse : `onSeekVoice`, `onCycleVoiceRate`), les liens du texte qui se touchent (`onPressLink`),
 * « Renvoyer » un envoi en échec, « X écrit… » (`typingLabel`) et les messages plus anciens
 * (`hasEarlier`).
 *
 * Contrôlé : le fil, le brouillon, l'envoi et le fil choisi arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutor } from "../../../ports/primary/ui/types"
import { type UILiveChatChannel, type UILiveChatChannelOption } from "../../../ports/primary/ui/types/studentLive"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { Avatar } from "../../atoms/Avatar"
import { Banner } from "../../atoms/Banner"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import {
    CONVERSATION_HEADER_AVATAR_SIZE,
    ConversationClose,
    ConversationHeader,
} from "../../molecules/ConversationHeader"
import { LiveChatChannels } from "../../molecules/LiveChatChannels"
import { SPEAKER_AVATAR_SIZE } from "../../molecules/MessageRow"
import { type ComposerAttachSource } from "../../molecules/MessageComposer"
import { ConversationPanel } from "../ConversationPanel"
import { type ConversationThreadProps, type UIConversationDay } from "../ConversationThread"

// Le fil ouvert et les puces : leur forme vit dans `ports/primary/ui/types` (socle du live).
export type { UILiveChatChannel, UILiveChatChannelOption }

export type LiveChatProps = {
    /** Le tuteur de la séance : l'en-tête d'un fil seul, l'interlocuteur de « Tous ». */
    tutor: UITutor
    /**
     * Qui lit le chat. `tutor` : le masteur — « Tous » s'écrit et se nomme « Tous » (le masteur n'est
     * jamais son propre interlocuteur), chaque élève a son fil (`student` sur sa puce), sans capsule.
     * Défaut `student` : le chat de l'élève, inchangé.
     */
    viewer?: "student" | "tutor"
    days: UIConversationDay[]
    /**
     * Les puces, dans leur ordre, chacune avec ses non-lus. Moins de deux (défaut) : pas de puce. Vu du
     * masteur, la puce d'un élève dit aussi sa présence et sa main levée (`student.online`,
     * `student.handRaised`, rendus par `<LiveChatChannels>`) ; absents, rien ne change.
     */
    channels?: UILiveChatChannelOption[]
    /** La puce ouverte. Défaut : la première. */
    channel?: UILiveChatChannel
    onChangeChannel?: (channel: UILiveChatChannel) => void
    /** Le fil ouvert n'a pas encore son premier relevé : sa forme. Défaut `false`. */
    loading?: boolean
    /**
     * Le fil ouvert se lit sans s'écrire : pas de composeur. Défaut : « Tous ». Le moteur le dit aussi
     * de la puce du tuteur de la séance quand il n'a pas de fil privé (elle ouvre le fil du groupe).
     */
    readOnly?: boolean
    draft: string
    onChangeDraft: (text: string) => void
    onSend: () => void
    /** L'envoi part. Défaut `false`. */
    sending?: boolean
    /** Les pièces jointes offertes, dans l'ordre. Absent ou vide (défaut) : texte seul. */
    attachSources?: ComposerAttachSource[]
    onAttach?: (source: ComposerAttachSource) => void
    /** Une pièce part. Défaut `false`. */
    attaching?: boolean
    /** Une pièce touchée (photo, document) : l'écran l'ouvre. */
    onOpenAttachment?: ConversationThreadProps["onOpenAttachment"]
    /** Le vocal qui joue, et le toucher qui le lance ou l'arrête. */
    voicePlayback?: ConversationThreadProps["voicePlayback"]
    onToggleVoice?: ConversationThreadProps["onToggleVoice"]
    /** Le vocal chargé se règle : sa position et sa vitesse. Absents (défaut) : lecture seule. */
    onSeekVoice?: ConversationThreadProps["onSeekVoice"]
    onCycleVoiceRate?: ConversationThreadProps["onCycleVoiceRate"]
    /** Un lien du texte touché : l'écran l'ouvre. Absent (défaut) : les textes restent simples. */
    onPressLink?: ConversationThreadProps["onPressLink"]
    /** « Renvoyer » un envoi en échec. */
    onRetryMessage?: ConversationThreadProps["onRetryMessage"]
    /** L'autre écrit : « Camille écrit… ». Absent : pas de points. */
    typingLabel?: string
    /** Les messages plus anciens, en tête du fil. */
    hasEarlier?: boolean
    loadingEarlier?: boolean
    onLoadEarlier?: () => void
    onClose: () => void
    testID?: string
}

const TEXTS = {
    title: "Messages",
    group: "Tous",
    close: "Fermer le chat",
    placeholder: "Écrire un message...",
    emptyTitle: "Aucun message pour l'instant",
    emptyTutor: (name: string) => `Une question ? Écris à ${name}.`,
    emptyGroup: "Ici, ton masteur écrit à tout le groupe.",
    groupReadOnly: "Seul ton masteur écrit dans « Tous ».",
    tutorReadOnly: (name: string) => `Pour l'instant, seul ${name} écrit ici.`,
    writeTo: (name: string) => `Écrire à ${name}`,
    tutorEmptyGroup: "Ici, tu écris à tout le groupe.",
    tutorEmptyStudent: (name: string) => `Écris à ${name}, lui seul lira.`,
}

const NO_CHANNELS: UILiveChatChannelOption[] = []

export function LiveChat(props: LiveChatProps) {
    const {
        tutor,
        viewer = "student",
        days,
        channels = NO_CHANNELS,
        channel,
        onChangeChannel,
        loading = false,
        readOnly: readOnlyProp,
        draft,
        onChangeDraft,
        onSend,
        sending = false,
        attachSources,
        onAttach,
        attaching = false,
        onOpenAttachment,
        voicePlayback,
        onToggleVoice,
        onSeekVoice,
        onCycleVoiceRate,
        onPressLink,
        onRetryMessage,
        typingLabel,
        hasEarlier = false,
        loadingEarlier = false,
        onLoadEarlier,
        onClose,
        testID,
    } = props
    const tutorName = tutorShortName(tutor)
    const open = channels.find((option) => option.id === channel) ?? channels[0]
    // Une puce sans tuteur ni élève, c'est « Tous ».
    const group = open != null && open.tutor == null && open.student == null
    const asTutor = viewer === "tutor"
    // À qui parle le fil, décidé une fois : l'élève de sa puce (vu du masteur, sans capsule) ; sinon le
    // tuteur de sa puce ; « Tous » garde le tuteur de la séance.
    const student = asTutor ? open?.student : undefined
    const interlocutorTutor = open?.tutor ?? tutor
    // Le masteur n'est jamais son propre interlocuteur : dans « Tous », il parle au groupe, qui s'appelle « Tous ».
    const masteurGroup = asTutor && (group || open == null)
    const interlocutor =
        student != null
            ? { name: student.firstname, firstname: student.firstname, avatarUrl: student.avatarUrl, capsule: false }
            : masteurGroup
              ? { name: TEXTS.group, firstname: TEXTS.group, avatarUrl: undefined, capsule: false }
              : {
                    name: tutorShortName(interlocutorTutor),
                    firstname: interlocutorTutor.firstname,
                    avatarUrl: interlocutorTutor.avatarUrl,
                    capsule: true,
                }
    // Avec les puces, ce sont elles qui disent à qui l'on écrit : l'en-tête ne le redit pas.
    const withChannels = channels.length > 1 && onChangeChannel != null
    // « Tous » se lit seulement : le lien ouvre le fil du tuteur de la séance, la première puce à tuteur.
    const tutorChannel = channels.find((option) => option.tutor != null)
    // Chez l'élève, « Tous » se lit seulement ; le masteur, lui, y écrit à tout le groupe.
    const readOnly = readOnlyProp ?? (group && !asTutor)
    const groupNotice = asTutor ? undefined : group ? (
        <Stack gap="1" align="center">
            <Banner icon="info-circle" color="text-muted" align="center" message={TEXTS.groupReadOnly} />
            {tutorChannel != null && onChangeChannel != null && (
                <TextButton
                    label={TEXTS.writeTo(tutorShortName(tutorChannel.tutor ?? tutor))}
                    onPress={() => onChangeChannel(tutorChannel.id)}
                />
            )}
        </Stack>
    ) : readOnly ? (
        <Banner icon="info-circle" color="text-muted" align="center" message={TEXTS.tutorReadOnly(interlocutor.name)} />
    ) : undefined

    return (
        <View style={styles.root} testID={testID}>
            <ConversationHeader
                bare
                name={withChannels ? TEXTS.title : asTutor ? interlocutor.name : tutorName}
                avatar={
                    withChannels ? undefined : asTutor ? (
                        <Avatar
                            uri={interlocutor.avatarUrl}
                            name={interlocutor.name}
                            size={CONVERSATION_HEADER_AVATAR_SIZE}
                        />
                    ) : (
                        <Avatar
                            uri={tutor.avatarUrl}
                            name={tutorName}
                            size={CONVERSATION_HEADER_AVATAR_SIZE}
                            variant="tutor"
                        />
                    )
                }
                end={<ConversationClose onPress={onClose} accessibilityLabel={TEXTS.close} />}
            />

            {withChannels && open != null && onChangeChannel != null && (
                <LiveChatChannels channels={channels} selected={open.id} onSelect={onChangeChannel} />
            )}

            {/*
             * Un fil par puce : changer de puce remonte le fil, qui s'ouvre en bas comme à la première
             * ouverture. Le même fil gardait la position et l'ancre de défilement du fil d'avant, et
             * s'ouvrait sur les messages les plus anciens.
             */}
            <ConversationPanel
                key={open?.id}
                thread={{
                    days,
                    interlocutor: {
                        speaker: "human",
                        name: interlocutor.name,
                        avatar: (
                            <Avatar
                                uri={interlocutor.avatarUrl}
                                name={interlocutor.name}
                                size={SPEAKER_AVATAR_SIZE}
                                variant={interlocutor.capsule ? "tutor" : undefined}
                            />
                        ),
                    },
                    loading,
                    emptyTitle: TEXTS.emptyTitle,
                    emptyMessage: asTutor
                        ? group
                            ? TEXTS.tutorEmptyGroup
                            : TEXTS.tutorEmptyStudent(interlocutor.firstname)
                        : group
                          ? TEXTS.emptyGroup
                          : TEXTS.emptyTutor(interlocutor.firstname),
                    onOpenAttachment,
                    voicePlayback,
                    onToggleVoice,
                    onSeekVoice,
                    onCycleVoiceRate,
                    onPressLink,
                    onRetryMessage,
                    typingLabel,
                    hasEarlier,
                    loadingEarlier,
                    onLoadEarlier,
                }}
                notice={groupNotice}
                composer={
                    readOnly
                        ? undefined
                        : {
                              value: draft,
                              onChangeText: onChangeDraft,
                              onSend,
                              placeholder: TEXTS.placeholder,
                              sending,
                              attachSources,
                              onAttach,
                              attaching,
                          }
                }
            />
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    root: { flex: 1, minHeight: 0 },
}))
